import { Suspense, useEffect, useState } from "react";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import { IDevolutionDeposit } from "../../../types/devolution-deposit";
import { TableFilters, TableFiltersState } from "../../../common/components/ui/table/TableFilters";
import Loading from "../../../common/components/ui/loading/Loading";
import GenericTable from "../../../common/components/ui/table/GenericTable";
import TableFooter from "../../../common/components/ui/table/TableFooter";
import { getAllReturnOfDeposits } from "../../../common/services/devolution-deposit.api";
import FileUploadModal from "./modals/FileUploadModal";
import { useNavigate } from "react-router-dom";
import ModalCreateReturnofdeposit from "./modals/ModalCreateDevolutionDeposit";
import { formatDateNoHours } from "@/common/helpers/date.helpers";
import { formatCurrency } from "@/common/helpers/mask.helpers";
import { getCompanySigla } from "@/common/helpers/company.helpers";
import { useCompanyStore } from "@/common/states/store/Company";
import { getCompanyId } from "@/common/services/company.api";

const headers = {
  companyId: "Cidade",
  name: "Nome",
  partnerCompanyName: "Parceiro",
  licensePlate: "Placa",
  dateLocation: "Data de Locação",
  dateDevolution: "Data de Devolução do veículo",
  valueDeposit: "Valor a Devolver",
  dateDevolutionDeposit: "Dias restantes para devolução",
  file: "Arquivo",
};

export default function TableDevolutionDeposit() {
  const navigate = useNavigate();
  const company = useCompanyStore();
  const companyId = getCompanyId();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [devolutionDeposits, setDevolutionDeposits] = useState<IDevolutionDeposit[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false); const [selectedRow, setSelectedRow] = useState<IDevolutionDeposit | null>(null);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-createdAt",
  });

  const fetchDeposits = async (filters) => {
    setIsLoading(true);
    try {
      const response = await getAllReturnOfDeposits({
        ...filters,
        page,
        itemsPerPage: data.itemsPerPage,
        orderBy: "dateDevolutionDeposit",
      });
      if (response && response.ok) {
        const formattedData = response.data.map((item: IDevolutionDeposit) => {
          const prazoDevolucao = new Date(item.dateDevolutionDeposit);
          const hoje = new Date();
          hoje.setHours(0, 0, 0, 0);
          prazoDevolucao.setHours(0, 0, 0, 0);

          const diffTime = prazoDevolucao.getTime() - hoje.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          return {
            ...item,
            dateDevolutionDeposit:
              diffDays > 0
                ? `${formatDateNoHours(item.dateDevolutionDeposit)} - ${diffDays} dias restantes`
                : diffDays === 0
                  ? `${formatDateNoHours(item.dateDevolutionDeposit)} - Vence hoje`
                  : `${formatDateNoHours(item.dateDevolutionDeposit)} - Prazo vencido`,
            file: item.file ? (
              <a
                href={`https://assets.locavibe.com.br/${item.file}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                {item.file.split("/").reverse()[0]}
              </a>
            ) : (
              <button
                className="px-3 py-1 bg-locavibe text-white rounded-lg hover:bg-red-700"
                onClick={() => {
                  setSelectedRow(item);
                  setIsModalOpen(true);
                }}
              >
                Anexar comprovante
              </button>
            ),
            _diffDays: diffDays, // Campo auxiliar para ordenação
          };
        });

        // Ordena por dias restantes (mais próximo/vencido primeiro)
        const sortedData = formattedData.sort((a, b) => a._diffDays - b._diffDays);

        setData({
          itemsPerPage: data.itemsPerPage,
          totalItems: response.totalItems,
        });
        setDevolutionDeposits(sortedData as unknown as IDevolutionDeposit[]);
      } else {
        setDevolutionDeposits([]);
      }
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
      notifyError("Erro ao carregar as devoluções");
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    fetchDeposits(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, company, companyId]);


  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Devoluções de cauções"
          placeholder="Digite o nome ou plano."
          newItemLabel="Cadastrar Devolução"
          onNewItemClick={() => setIsCreateModalOpen(true)}
          statusOptions={{}}
          onSearch={setFilters}
          entity="vehicles"
          tableData={devolutionDeposits}
        />
        <GenericTable
          headers={headers}
          data={devolutionDeposits}
          setFilters={() => { }}
          renders={{
            companyId: (companyId) => {
              return getCompanySigla(companyId as string) || "";
            },
            name: (name, row) => {
              const deposit = row as IDevolutionDeposit;
              return (
                <a
                  href={`/clientes/${deposit.renterId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/clientes/${deposit.renterId}`);
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black hover:text-blue-500"
                >
                  {name as string}
                </a>
              );
            },
            licensePlate: (plate, row) => {
              const deposit = row as IDevolutionDeposit;
              return (
                <a
                  href={`/veiculos/${deposit.licensePlate}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/veiculos/${deposit.licensePlate}`);
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black hover:text-blue-500"
                >
                  {plate as string}
                </a>
              );
            },
            dateLocation: formatDateNoHours,
            dateDevolution: formatDateNoHours,
            valueDeposit: formatCurrency
          }}
          entity="vehicles"
        />

        {isLoading && <Loading />}

        {isCreateModalOpen && (
          <ModalCreateReturnofdeposit
            onClose={() => setIsCreateModalOpen(false)}
            onSave={async () => {
              await fetchDeposits(filters);
              setIsCreateModalOpen(false);
            }}
          />
        )}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />

        <FileUploadModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedRow={selectedRow}
          setDevolutionDeposits={setDevolutionDeposits}
          renterId={selectedRow?.renterId || ""}
        />
      </div>
    </Suspense>
  );
}
